import { IonIcon } from "@ionic/react";
import { checkmark } from "ionicons/icons";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";

interface QuestionnaireCompletionFeedbackContextValue {
  showCompletionFeedback(): void;
}

const QuestionnaireCompletionFeedbackContext =
  createContext<QuestionnaireCompletionFeedbackContextValue>({
    showCompletionFeedback: () =>
      console.warn("QuestionnaireCompletionFeedbackContext is not initialized"),
  });

const feedbackDuration = 1100;

const QuestionnaireCompletionFeedbackProvider: React.FC<{
  children: ReactNode;
  enabled?: boolean;
}> = ({ children, enabled = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const clearPendingTimers = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showCompletionFeedback = useCallback(() => {
    if (!enabled) return;

    clearPendingTimers();
    setIsVisible(false);

    animationFrameRef.current = window.requestAnimationFrame(() => {
      setIsVisible(true);
      timeoutRef.current = window.setTimeout(() => {
        setIsVisible(false);
        timeoutRef.current = null;
      }, feedbackDuration);
    });
  }, [clearPendingTimers, enabled]);

  useEffect(
    () => () => {
      clearPendingTimers();
    },
    [clearPendingTimers]
  );

  const contextValue = useMemo(
    () => ({ showCompletionFeedback }),
    [showCompletionFeedback]
  );

  return (
    <QuestionnaireCompletionFeedbackContext.Provider value={contextValue}>
      {children}
      {isVisible &&
        ReactDOM.createPortal(
          <FeedbackLayer aria-hidden="true">
            <FeedbackOrb>
              <FeedbackGlow />
              <FeedbackRing />
              <FeedbackSparkleTop />
              <FeedbackSparkleSide />
              <FeedbackIcon icon={checkmark} />
            </FeedbackOrb>
          </FeedbackLayer>,
          document.body
        )}
    </QuestionnaireCompletionFeedbackContext.Provider>
  );
};

const useQuestionnaireCompletionFeedback = () =>
  useContext(QuestionnaireCompletionFeedbackContext);

const floatFade = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, 18px, 0) scale(0.82);
  }

  18% {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }

  72% {
    opacity: 1;
    transform: translate3d(0, -6px, 0) scale(1);
  }

  100% {
    opacity: 0;
    transform: translate3d(0, -20px, 0) scale(0.94);
  }
`;

const pulse = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.72);
  }

  25% {
    opacity: 0.8;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(1.36);
  }
`;

const sparkle = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.4);
  }

  28% {
    opacity: 0.95;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: translate3d(0, -10px, 0) scale(0.7);
  }
`;

const FeedbackLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FeedbackOrb = styled.div`
  position: relative;
  width: 82px;
  height: 82px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Brand tint rides the EMPHASIS tone (strong, derived from primary —
     tokens.ts): alpha stops over a near-white base reproduce the old
     pastel-over-white look for any theme color. */
  background:
    radial-gradient(circle at 30% 28%, rgba(255, 255, 255, 0.98), transparent 32%),
    linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.9),
      rgba(var(--app-color-emphasis-rgb, var(--app-color-primary-rgb, 45, 182, 180)), 0.16) 42%,
      rgba(var(--app-color-emphasis-rgb, var(--app-color-primary-rgb, 45, 182, 180)), 0.6)
    ),
    linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92));
  border: 1px solid rgba(255, 255, 255, 0.75);
  box-shadow:
    0 10px 24px rgba(18, 71, 88, 0.12),
    0 24px 56px rgba(var(--app-color-emphasis-rgb, var(--app-color-primary-rgb, 45, 182, 180)), 0.22);
  backdrop-filter: blur(14px);
  animation: ${floatFade} ${feedbackDuration}ms cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const FeedbackGlow = styled.div`
  position: absolute;
  inset: -10px;
  border-radius: inherit;
  background: radial-gradient(
    circle,
    rgba(var(--app-color-emphasis-rgb, var(--app-color-primary-rgb, 168, 241, 233)), 0.4),
    rgba(var(--app-color-emphasis-rgb, var(--app-color-primary-rgb, 168, 241, 233)), 0) 68%
  );
  filter: blur(6px);
  animation: ${pulse} ${feedbackDuration}ms ease-out both;
`;

const FeedbackRing = styled.div`
  position: absolute;
  inset: -6px;
  border-radius: inherit;
  border: 1px solid rgba(var(--app-color-emphasis-rgb, var(--app-color-primary-rgb, 196, 247, 239)), 0.5);
  animation: ${pulse} ${feedbackDuration}ms ease-out both;
`;

const FeedbackSparkleTop = styled.div`
  position: absolute;
  top: 10px;
  right: 16px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 0 18px rgba(255, 255, 255, 0.95);
  animation: ${sparkle} ${feedbackDuration}ms ease-out both;
`;

const FeedbackSparkleSide = styled.div`
  position: absolute;
  bottom: 16px;
  left: 14px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 16px rgba(255, 255, 255, 0.78);
  animation: ${sparkle} ${feedbackDuration}ms ease-out both;
  animation-delay: 70ms;
`;

const FeedbackIcon = styled(IonIcon)`
  position: relative;
  z-index: 1;
  font-size: 34px;
  color: var(--app-color-emphasis, var(--app-color-primary, #167a74));
  filter: drop-shadow(0 4px 8px rgba(255, 255, 255, 0.4));
`;

export {
  QuestionnaireCompletionFeedbackProvider,
  useQuestionnaireCompletionFeedback,
};